import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Trash2, Check, AlertTriangle, X } from 'lucide-react';
import api from '../services/api';
import '../styles/GestioneStanze.css';
import { notificaAvviso } from '../utils/notifiche';

/**
 * GestioneStanze — stanze e apparecchiature.
 *
 * Il colore e' la cosa che conta davvero: identifica la colonna in
 * agenda, quindi due stanze con lo stesso colore rendono la griglia
 * ambigua. La schermata lo segnala invece di lasciartelo scoprire
 * guardando l'agenda.
 */

const VUOTA = {
    nome: '', colore: 'var(--rm-accento)', tipo: 'stanza', ordine: 0, attiva: true, note: '',
};

/**
 * Trentadue colori distinguibili fra loro, che e' il punto: in agenda il
 * colore identifica la colonna, e due tinte simili valgono quanto due
 * uguali. Sono piu' delle stanze esistenti, cosi' ce n'e' sempre uno libero.
 *
 * I primi diciotto sono quelli gia' in uso: alcune stanze si chiamano come
 * il proprio colore - Sala rossa, Studio azzurro, Studio giallo - e
 * cambiarglielo avrebbe reso il nome una bugia.
 */
/* I colori oggi assegnati alle stanze, piu' una riserva per quelle che
   verranno. Sono stati scelti misurando la distanza percettiva fra tutte
   le coppie, calcolata due volte: a vista normale e simulando la
   deuteranopia, la forma piu' comune di daltonismo. Prima tredici coppie
   erano identiche e ventitre' indistinguibili in una griglia; ora nessuna
   e' identica e ne resta una sola sotto soglia.

   Quella coppia e' Studio arancio / Studio giallo, e non si tocca: sono
   due stanze il cui nome E' il loro colore. Allontanarle vorrebbe dire
   chiamarle diversamente.

   Con ventinove stanze il colore non basta a distinguerle tutte a colpo
   d'occhio: di colori riconoscibili senza pensarci ne esistono sedici. Le
   apparecchiature stanno quindi su toni piu' spenti e freddi degli studi,
   cosi' l'occhio legge prima la categoria e poi il nome.

   Resta modificabile: la segreteria puo' cambiare qualunque colore, e la
   schermata avvisa se ne sceglie uno gia' usato da un'altra stanza. */
const PALETTE = [
    '#642555', '#549175', '#8f9432', '#342f91', '#553c86', '#2e2c62',
    '#376c57', '#364c70', '#473ab4', '#516223', '#70608a', '#612020',
    '#b6b66c', '#6b6d4b', '#282672', '#895b7a', '#64233a', '#b663a5',
    '#7b1fa2', '#d9534f', '#e8944a', '#5aa9e6', '#eceff1', '#3a6ea5',
    '#e3b34a', '#8a9199', '#9b8bc4', '#e08fa8', '#5fa672',
    // riserva per le stanze future
    '#a0522d', '#6b8e23', '#2f6f4f', '#00838f', '#1565c0', '#4527a0',
    '#ad1457', '#b71c1c', '#ef6c00', '#f9a825', '#827717', '#37474f',
    '#795548', '#546e7a', '#4e342e', '#00695c',
];

const GestioneStanze = () => {
    const [stanze, setStanze] = useState([]);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [salvato, setSalvato] = useState(false);
    const [modifica, setModifica] = useState(null);   // stanza in scheda, o VUOTA

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await api.get('/stanze');
            setStanze(r.data.data || []);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a caricare le stanze');
        } finally {
            setCaricamento(false);
        }
    }, []);

    useEffect(() => { carica(); }, [carica]);

    /* Quali colori compaiono piu' di una volta fra le stanze attive. */
    /* Per colore, i nomi delle stanze attive che lo usano: serve sia
       all'avviso in testa sia alla palette, che deve poter dire "questo e'
       gia' di X" invece di lasciar scoprire il doppione in agenda. */
    const perColore = useMemo(() => {
        const m = {};
        stanze.filter((s) => s.attiva).forEach((s) => {
            const c = (s.colore || '').toLowerCase();
            (m[c] = m[c] || []).push(s.nome);
        });
        return m;
    }, [stanze]);

    const doppi = useMemo(
        () => new Set(Object.keys(perColore).filter((c) => perColore[c].length > 1)),
        [perColore]
    );

    /* Il primo colore della palette che non usa nessun altro. */
    const primoLibero = useMemo(
        () => PALETTE.find((c) => !perColore[c.toLowerCase()]) || null,
        [perColore]
    );

    const salva = async () => {
        setErrore(null);
        try {
            if (modifica.id) await api.put(`/stanze/${modifica.id}`, modifica);
            else await api.post('/stanze', modifica);
            setModifica(null);
            setSalvato(true);
            setTimeout(() => setSalvato(false), 1500);
            await carica();
        } catch (e) {
            setErrore(e.response?.data?.message || 'Errore nel salvataggio');
        }
    };

    const elimina = async (s) => {
        if (!window.confirm(`Eliminare "${s.nome}"?`)) return;
        setErrore(null);
        try {
            const r = await api.delete(`/stanze/${s.id}`);
            if (r.data.eliminata === false) notificaAvviso(r.data.message);
            await carica();
        } catch (e) {
            setErrore(e.response?.data?.message || 'Errore nella cancellazione');
        }
    };

    const gruppi = [
        { tipo: 'stanza', titolo: 'Stanze' },
        { tipo: 'apparecchiatura', titolo: 'Apparecchiature' },
    ];

    return (
        <div className="st-wrap">
            <header className="st-testata">
                <div>
                    <h1>Stanze e apparecchiature</h1>
                    <p>Il colore identifica la colonna in agenda.</p>
                </div>
                <div className="st-azioni">
                    {salvato && <span className="st-salvato"><Check size={15} /> salvato</span>}
                    <button className="st-btn primario" onClick={() => setModifica({ ...VUOTA })}>
                        <Plus size={15} /> Nuova
                    </button>
                </div>
            </header>

            {errore && <div className="st-errore">{errore}</div>}

            {doppi.size > 0 && (
                <div className="st-avviso">
                    <AlertTriangle size={15} />
                    <div>
                        <b>In agenda queste colonne si confondono:</b>
                        <ul className="st-doppi">
                            {[...doppi].map((c) => (
                                <li key={c}>
                                    <span className="st-pallino" style={{ background: c }} />
                                    {perColore[c].join(', ')}
                                </li>
                            ))}
                        </ul>
                        {primoLibero && (
                            <span className="st-suggerimento">
                                Apri una di queste e scegli un colore libero: il primo è
                                <span className="st-pallino" style={{ background: primoLibero }} />
                                {primoLibero}.
                            </span>
                        )}
                    </div>
                </div>
            )}

            {caricamento && <p className="st-vuoto">Caricamento…</p>}

            {!caricamento && gruppi.map((g) => {
                const righe = stanze.filter((s) => s.tipo === g.tipo);
                if (!righe.length) return null;
                return (
                    <section key={g.tipo} className="st-gruppo">
                        <h2>{g.titolo} <span>{righe.length}</span></h2>
                        <div className="st-lista">
                            {righe.map((s) => (
                                <div
                                    key={s.id}
                                    className={`st-riga ${s.attiva ? '' : 'spenta'}`}
                                    onClick={() => setModifica({ ...s, note: s.note || '' })}
                                >
                                    <span
                                        className={`st-pastiglia ${doppi.has((s.colore || '').toLowerCase()) ? 'doppio' : ''}`}
                                        style={{ background: s.colore }}
                                        title={doppi.has((s.colore || '').toLowerCase())
                                            ? 'Colore usato anche da un\'altra stanza' : s.colore}
                                    />
                                    <span className="st-nome">{s.nome}</span>
                                    <span className="st-codice">{s.colore}</span>
                                    <span className="st-usi">
                                        {s.n_orari > 0 && `${s.n_orari} orari`}
                                        {s.n_orari > 0 && s.n_prestazioni > 0 && ' · '}
                                        {s.n_prestazioni > 0 && `${s.n_prestazioni} prestazioni`}
                                    </span>
                                    {!s.attiva && <span className="st-tag">disattivata</span>}
                                    <button
                                        className="st-icona pericolo"
                                        title="Elimina o disattiva"
                                        onClick={(e) => { e.stopPropagation(); elimina(s); }}
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })}

            {modifica && (
                <div className="st-modale-fondo" onClick={() => setModifica(null)}>
                    <div className="st-modale" onClick={(e) => e.stopPropagation()}>
                        <header>
                            <h3>{modifica.id ? 'Modifica' : 'Nuova stanza'}</h3>
                            <button onClick={() => setModifica(null)}><X size={18} /></button>
                        </header>

                        <label className="st-campo">
                            <span>Nome</span>
                            <input
                                value={modifica.nome}
                                maxLength={60}
                                onChange={(e) => setModifica({ ...modifica, nome: e.target.value })}
                                placeholder="Studio lilla"
                            />
                        </label>

                        <label className="st-campo">
                            <span>Colore in agenda</span>
                            <div className="st-palette">
                                {PALETTE.map((c) => {
                                    /* Chi usa gia' questo colore, escluso chi si sta
                                       modificando: altrimenti la sua stessa stanza
                                       risulterebbe un doppione di se' stessa. */
                                    const altri = (perColore[c.toLowerCase()] || [])
                                        .filter((n) => n !== modifica.nome);
                                    const scelto = (modifica.colore || '').toLowerCase() === c.toLowerCase();
                                    return (
                                        <button
                                            key={c}
                                            type="button"
                                            className={`st-swatch${scelto ? ' scelto' : ''}${altri.length ? ' preso' : ''}`}
                                            style={{ background: c }}
                                            onClick={() => setModifica({ ...modifica, colore: c })}
                                            title={altri.length ? `Già di: ${altri.join(', ')}` : c}
                                            aria-label={altri.length ? `${c}, già di ${altri.join(', ')}` : c}
                                        />
                                    );
                                })}
                            </div>
                            {(perColore[(modifica.colore || '').toLowerCase()] || [])
                                .filter((n) => n !== modifica.nome).length > 0 && (
                                <span className="st-preso-avviso">
                                    <AlertTriangle size={13} /> Questo colore è già di{' '}
                                    {(perColore[(modifica.colore || '').toLowerCase()] || [])
                                        .filter((n) => n !== modifica.nome).join(', ')}
                                </span>
                            )}
                            <div className="st-colore">
                                <input
                                    type="color"
                                    value={modifica.colore}
                                    onChange={(e) => setModifica({ ...modifica, colore: e.target.value })}
                                />
                                <input
                                    className="st-esa"
                                    value={modifica.colore}
                                    onChange={(e) => setModifica({ ...modifica, colore: e.target.value })}
                                    placeholder="var(--rm-accento)"
                                />
                            </div>
                        </label>

                        <div className="st-campi-doppi">
                            <label className="st-campo">
                                <span>Tipo</span>
                                <select
                                    value={modifica.tipo}
                                    onChange={(e) => setModifica({ ...modifica, tipo: e.target.value })}
                                >
                                    <option value="stanza">Stanza</option>
                                    <option value="apparecchiatura">Apparecchiatura</option>
                                </select>
                            </label>
                            <label className="st-campo">
                                <span>Ordine</span>
                                <input
                                    type="number"
                                    value={modifica.ordine}
                                    onChange={(e) => setModifica({ ...modifica, ordine: e.target.value })}
                                />
                            </label>
                        </div>

                        <label className="st-campo">
                            <span>Note</span>
                            <input
                                value={modifica.note || ''}
                                onChange={(e) => setModifica({ ...modifica, note: e.target.value })}
                            />
                        </label>

                        <label className="st-campo-riga">
                            <input
                                type="checkbox"
                                checked={modifica.attiva !== false}
                                onChange={(e) => setModifica({ ...modifica, attiva: e.target.checked })}
                            />
                            <span>Attiva</span>
                        </label>

                        <footer>
                            <button className="st-btn" onClick={() => setModifica(null)}>Annulla</button>
                            <button className="st-btn primario" onClick={salva}>Salva</button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestioneStanze;
